import { useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';

import { getDatadogClientToken } from 'hooks/useAwsRequests';

import { isLocalEnvironment } from '../utils';

function startDatadog(clientToken: string) {
  if (datadogRum.getInternalContext()?.application_id) {
    return;
  }

  datadogRum.init({
    applicationId: process.env.REACT_APP_DATADOG_ID || '',
    clientToken: clientToken,
    site: 'datadoghq.com',
    service: 'oms-ui',
    env: process.env.REACT_APP_DATADOG_ENV,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    trackViewsManually: false,
  });
}

const identifyDatadogUser = async (userEmail: string) => {
  if (isLocalEnvironment()) {
    return; // skip Datadog events when running locally
  }

  datadogRum.setUser({
    email: userEmail,
    name: userEmail,
    id: userEmail,
  });
};

export function useDatadog() {
  const fetchFlag = useRef(false);

  const initDatadogSession = async () => {
    if (isLocalEnvironment() || fetchFlag.current) {
      return;
    }

    fetchFlag.current = true;
    try {
      const token = await getDatadogClientToken();
      if (token) {
        startDatadog(token);
      }
    } catch (err: any) {
      console.error('ERROR: ', err?.message);
    } finally {
      if (!datadogRum.getInternalContext()?.application_id) {
        fetchFlag.current = false;
      }
    }
  };

  return {
    initDatadogSession,
    identifyDatadogUser,
  };
}
